// Payloads for RTableGroup operations, and their format validators.
//
// An RTableGroup owns one physical DAG: it is the unit of atomicity, snapshot,
// observation and composition. Member RTables live on scoped projections of
// this DAG; a group position is a consistent snapshot of all member tables.
//
// The group observes its RSchema (and any foreign groups referenced by
// cross-group FKs) via ref-advance ops; the canonical ref-advance payload from
// the mvt module is reused as-is. The group's barrier ref-advance to a new
// RSchema version is the schema deploy moment. Deploy authority is the
// group's own policy: the create payload may carry a `canDeploy` predicate
// ('object' context: $author available, no subject row), evaluated against
// the ref-advance author at the op's position; the ref-advance op carries
// author/signature as extra fields (the canonical format is checked
// non-strictly to allow this). Like bindings, canDeploy is fixed at creation
// in v1; rotating deploy authority = granting/revoking cap rows it points at.
import { json } from "@hyper-hyper-space/hhs3_json";
import { createPayloadTypeFormat } from "@hyper-hyper-space/hhs3_mvt";
import { MAX_NAME_LENGTH, MAX_QUALIFIED_NAME_LENGTH, MAX_TABLES, MAX_SEED_LENGTH, MAX_HASH_ALGORITHM_LENGTH, MAX_HASH_LENGTH, } from "../rschema/payload.js";
export const MAX_INITIAL_ROWS_PER_TABLE = 1024;
export const MAX_BINDINGS = 256;
export const MAX_BUNDLE_OPS = 1024;
// Create a table group:
// `schemaRef` + `schemaVersion` pin the RSchema (id and initial version) this
// group instance deploys. Validating a group create therefore requires
// resolving the RSchema at the pinned version (a cross-DAG dependency at
// genesis).
//
// `initialRows` are genesis fiat rows, keyed by table: they are given, not
// validated as ops (no preconditions, no signatures), like RCap's irrevocable
// creators. They solve the permissions bootstrap: a pre-filled admin or
// capability row roots every delegation chain, and identity rows carry public
// keys as column values (solving keyId -> publicKey lookup). Each entry uses
// the insert row op shape.
//
// `bindings` fix the group-name resolution for qualified FK targets
// ('group.table') and gate targets, by value, at creation time: name ->
// concrete group object id. v1 bindings are immutable; mutation via barrier
// ops may come later. A binding whose target object is not present in the
// replica is an infrastructure error (throw), never an MVT data condition.
// Bindings must be INJECTIVE (name -> id one-to-one), enforced at create:
// gates and name resolution are keyed per name, but a ref-advance / observe
// op is keyed by object id, so the reverse map (id -> name) must be
// unambiguous to attribute an observe to a single binding name and its gate.
//
// `canObserve` is an optional per-binding predicate gating foreign-group
// observations (ref-advances of a bound group id). It is keyed by binding NAME
// (resolved to id through the injective bindings map) and evaluated in 'object'
// context ($author only, no subject row) in the OBSERVED group's frame at the
// observed foreign version. A binding with no canObserve entry is ungated
// (observation needs no authority, as before). Enforced both at write-admission
// (validation, against the imported version) and at-use (view-time, against the
// G-upward stratified observed version), so a back-dated observation by a former
// principal is voided. Fixed v1 like bindings / canDeploy.
//
// `idProvider` selects this group's identity provider for signature
// verification: a LOCAL table name (must exist in the pinned schema and be
// flagged idProvider) or a qualified 'group.table' (name-resolvability only at
// create — its group-name must be in `bindings`; the foreign table being a
// provider is a runtime concern). Fixed v1 like bindings / canDeploy. A group
// with no idProvider performs no authentication (claimed authors are trusted).
export const RTABLE_GROUP_TYPE_ID = 'hhs/rtable_group_v1';
export const createTableGroupFormat = {
    action: [json.Type.Constant, 'create'],
    type: createPayloadTypeFormat(RTABLE_GROUP_TYPE_ID),
    name: [json.Type.BoundedString, MAX_NAME_LENGTH],
    seed: [json.Type.BoundedString, MAX_SEED_LENGTH],
    schemaRef: [json.Type.BoundedString, MAX_HASH_LENGTH],
    schemaVersion: json.Type.Something,
    initialRows: [json.Type.Option, [json.Type.BoundedMap,
            [json.Type.BoundedString, MAX_NAME_LENGTH],
            [json.Type.BoundedArray, json.Type.Something, MAX_INITIAL_ROWS_PER_TABLE],
            MAX_TABLES]],
    bindings: [json.Type.Option, [json.Type.BoundedMap,
            [json.Type.BoundedString, MAX_NAME_LENGTH],
            [json.Type.BoundedString, MAX_HASH_LENGTH],
            MAX_BINDINGS]],
    canDeploy: [json.Type.Option, json.Type.Something], // checked with validatePredicate('object')
    canObserve: [json.Type.Option, [json.Type.BoundedMap,
            [json.Type.BoundedString, MAX_NAME_LENGTH],
            json.Type.Something, // each checked with validatePredicate('object')
            MAX_BINDINGS]],
    idProvider: [json.Type.Option, [json.Type.BoundedString, MAX_QUALIFIED_NAME_LENGTH]],
    hashAlgorithm: [json.Type.Option, [json.Type.BoundedString, MAX_HASH_ALGORITHM_LENGTH]],
};
export const rowEnvelopeFormat = {
    action: [json.Type.Constant, 'row'],
    table: [json.Type.BoundedString, MAX_NAME_LENGTH],
    op: json.Type.Something,
};
export const bundleWriteFormat = {
    table: [json.Type.BoundedString, MAX_NAME_LENGTH],
    op: json.Type.Something,
};
export const bundleFormat = {
    action: [json.Type.Constant, 'bundle'],
    writes: [json.Type.BoundedArray, bundleWriteFormat, MAX_BUNDLE_OPS],
};
